import { addPence, pence, subPence, ZERO_PENCE } from '@cf/shared';
import { assertReviewed } from './review.js';
import { clampTo, excessOver, minPence, mulBps } from './bps.js';
/**
 * Split `amount` of taxable income starting at cumulative position `from`
 * across the (possibly extended) bands.
 */
function sliceBands(amount, from, basicTop, additionalThreshold) {
    const end = addPence(from, amount);
    const basic = clampTo(subPence(minPence(end, basicTop), from), amount);
    const higherStart = from > basicTop ? from : basicTop;
    const higher = clampTo(subPence(minPence(end, additionalThreshold), higherStart), subPence(amount, basic));
    const additional = pence(Math.max(0, end - Math.max(from, additionalThreshold)));
    return { basic, higher, additional };
}
export function incomeTax(rules, input) {
    assertReviewed(rules);
    const r = rules.incomeTax;
    const grossPension = input.grossPensionContributions ?? ZERO_PENCE;
    const otherDeductions = input.otherAniDeductions ?? ZERO_PENCE;
    for (const [label, v] of Object.entries({
        nonSavings: input.nonSavings,
        savings: input.savings,
        dividends: input.dividends,
        grossPensionContributions: grossPension,
        otherAniDeductions: otherDeductions,
    })) {
        if (!Number.isSafeInteger(v) || v < 0) {
            throw new RangeError(`incomeTax input ${label} must be a non-negative integer of pence`);
        }
    }
    const totalIncome = addPence(addPence(input.nonSavings, input.savings), input.dividends);
    // Personal allowance taper on adjusted net income.
    const adjustedNetIncome = pence(Math.max(0, totalIncome - grossPension - otherDeductions));
    const taperExcess = excessOver(adjustedNetIncome, r.personalAllowanceTaperThreshold);
    const paReduction = pence(Math.trunc(taperExcess / 2));
    const personalAllowance = pence(Math.max(0, r.personalAllowance - paReduction));
    // Relief at source: gross contributions extend the basic band (and the
    // additional-rate threshold) — this is the higher/additional-rate relief.
    const basicTop = addPence(r.basicBandTop, grossPension);
    const additionalThreshold = addPence(r.additionalRateThreshold, grossPension);
    // Allocate the allowance non-savings → savings → dividends.
    const paToNonSavings = minPence(personalAllowance, input.nonSavings);
    const paToSavings = minPence(subPence(personalAllowance, paToNonSavings), input.savings);
    const paToDividends = minPence(subPence(subPence(personalAllowance, paToNonSavings), paToSavings), input.dividends);
    const taxableNonSavings = subPence(input.nonSavings, paToNonSavings);
    const taxableSavings = subPence(input.savings, paToSavings);
    const taxableDividends = subPence(input.dividends, paToDividends);
    const taxableIncome = addPence(addPence(taxableNonSavings, taxableSavings), taxableDividends);
    let position = ZERO_PENCE; // cumulative taxable income already stacked
    let marginalBand = 'none';
    const noteBand = (slices) => {
        if (slices.additional > 0)
            marginalBand = 'additional';
        else if (slices.higher > 0 && marginalBand !== 'additional')
            marginalBand = 'higher';
        else if (slices.basic > 0 && marginalBand === 'none')
            marginalBand = 'basic';
    };
    // 1. Non-savings income.
    const ns = sliceBands(taxableNonSavings, position, basicTop, additionalThreshold);
    noteBand(ns);
    const nonSavingsTax = addPence(addPence(mulBps(ns.basic, r.basicRateBps), mulBps(ns.higher, r.higherRateBps)), mulBps(ns.additional, r.additionalRateBps));
    position = addPence(position, taxableNonSavings);
    // 2. Savings income: starting rate band, then PSA, then band rates. Both
    // nil-rate amounts still consume band room.
    const startingRateAvailable = pence(Math.max(0, r.savingsStartingRateBand - taxableNonSavings));
    const savingsAtStartingRate = minPence(taxableSavings, startingRateAvailable);
    // PSA is set by the band the person's total taxable income reaches.
    const totalForPsa = taxableIncome;
    const psa = totalForPsa > additionalThreshold
        ? r.personalSavingsAllowanceAdditional
        : totalForPsa > basicTop
            ? r.personalSavingsAllowanceHigher
            : r.personalSavingsAllowanceBasic;
    const savingsAtPsa = minPence(subPence(taxableSavings, savingsAtStartingRate), psa);
    const savingsTaxable = subPence(subPence(taxableSavings, savingsAtStartingRate), savingsAtPsa);
    position = addPence(position, addPence(savingsAtStartingRate, savingsAtPsa));
    const sv = sliceBands(savingsTaxable, position, basicTop, additionalThreshold);
    noteBand(sv);
    const savingsTax = addPence(addPence(mulBps(sv.basic, r.basicRateBps), mulBps(sv.higher, r.higherRateBps)), mulBps(sv.additional, r.additionalRateBps));
    position = addPence(position, savingsTaxable);
    // 3. Dividends: allowance is a nil-rate band that consumes band room.
    const dividendAtAllowance = minPence(taxableDividends, r.dividendAllowance);
    position = addPence(position, dividendAtAllowance);
    const dv = sliceBands(subPence(taxableDividends, dividendAtAllowance), position, basicTop, additionalThreshold);
    noteBand(dv);
    const dividendTax = addPence(addPence(mulBps(dv.basic, r.dividendBasicRateBps), mulBps(dv.higher, r.dividendHigherRateBps)), mulBps(dv.additional, r.dividendAdditionalRateBps));
    return {
        personalAllowance,
        taxableIncome,
        nonSavingsTax,
        savingsTax,
        dividendTax,
        totalTax: addPence(addPence(nonSavingsTax, savingsTax), dividendTax),
        marginalBand,
        extendedBasicBandTop: basicTop,
        bands: {
            nonSavings: ns,
            savings: {
                ...sv,
                atStartingRate: savingsAtStartingRate,
                atPersonalSavingsAllowance: savingsAtPsa,
            },
            dividends: { ...dv, atDividendAllowance: dividendAtAllowance },
        },
    };
}
//# sourceMappingURL=income-tax.js.map